<template>
  <div id="app">
    <header v-if="showNavbar" class="main-header" :class="{ 'scrolled': isScrolled }">
      <nav class="main-nav">
        <router-link to="/" class="nav-logo">天狗商城</router-link>
        <div class="nav-links">
          <template v-if="!isAdmin">
            <router-link to="/" class="nav-link">首页</router-link>
            <router-link to="/products" class="nav-link">商品分类</router-link>
            <router-link to="/hot" class="nav-link">热卖商品</router-link>
          </template>
        </div>
        <div class="nav-user">
          <template v-if="isLoggedIn">
            <div class="user-menu">
              <img :src="currentUser.avatarUrl || 'https://placehold.co/100x100/EFEFEF/AAAAAA?text=A'" alt="User Avatar" class="user-avatar">
              <span class="welcome-text">{{ currentUser.nickname }}</span>
              <div class="dropdown">
                <template v-if="!isAdmin">
                  <router-link to="/profile/basic" class="dropdown-item">个人资料</router-link>
                  <router-link to="/orders" class="dropdown-item">我的订单</router-link>
                  <router-link to="/cart" class="dropdown-item">我的购物车</router-link>
                  <div class="divider"></div>
                </template>
                <a href="#" @click.prevent="logout" class="dropdown-item">退出登录</a>
              </div>
            </div>
          </template>
          <template v-else>
            <router-link to="/login" class="nav-button login">登录</router-link>
            <router-link to="/register" class="nav-button register">注册</router-link>
          </template>
        </div>
      </nav>
    </header>

    <main class="main-content" :class="{ 'no-padding-top': !showNavbar }">
      <router-view/>
    </main>
    
    <FooterComponent v-if="showFooter" />

    <ToastNotification />
    <ConfirmModal />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import FooterComponent from './components/FooterComponent.vue';
import ToastNotification from './components/ToastNotification.vue';
import ConfirmModal from './components/ConfirmModal.vue';

const isAdmin = computed(() => store.getters.isAdmin);

const store = useStore();
const router = useRouter();
const route = useRoute();
const isScrolled = ref(false);

const isLoggedIn = computed(() => store.getters.isLoggedIn);
const currentUser = computed(() => store.getters.currentUser);

const showNavbar = computed(() => !route.meta.hideNavbar);
// 【新增】创建一个计算属性，判断当前路由是否需要隐藏页脚
const showFooter = computed(() => !route.meta.hideFooter);

const handleScroll = () => {
  isScrolled.value = window.scrollY > 10;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

const logout = () => {
  store.dispatch('logout');
  localStorage.removeItem('jwt_token'); 
  router.push('/login');
};
</script>

<style>
body {
  margin: 0;
  font-family: "Helvetica Neue", Helvetica, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "微软雅黑", Arial, sans-serif;
  background-color: #f8f9fa;
  color: #333;
  line-height: 1.6;
}

#app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.main-content {
  flex-grow: 1;
}

.page-container {
  max-width: 1200px;
  margin: 20px auto;
  padding: 0 15px;
}

.section-title {
  text-align: center;
  font-size: 1.8em;
  font-weight: 600;
  margin-bottom: 30px;
  color: #2c3e50;
  position: relative;
  padding-bottom: 15px;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, #5e72e4, #9b59b6);
  border-radius: 3px;
}

.main-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 0 20px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.4s ease-in-out;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
}

.main-header.scrolled {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
}

.main-nav {
  display: flex;
  align-items: center;
  height: 80px;
  max-width: 1200px;
  margin: 0 auto;
  transition: height 0.4s ease-in-out;
}

.main-header.scrolled .main-nav {
  height: 64px;
}

.nav-logo {
  font-size: 1.8rem;
  font-weight: 700;
  color: #fff;
  text-decoration: none;
  transition: color 0.3s;
}

.main-header.scrolled .nav-logo {
  color: #5e72e4;
}

.nav-links {
  margin-left: 50px;
  display: flex;
  align-items: center;
}

.nav-link {
  margin: 0 18px;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  transition: all 0.3s;
  position: relative;
  padding: 5px 0;
}

.main-header.scrolled .nav-link {
  color: #495057;
}

.nav-link:hover,
.nav-link.router-link-exact-active {
  color: #fff;
}

.main-header.scrolled .nav-link:hover,
.main-header.scrolled .nav-link.router-link-exact-active {
  color: #5e72e4;
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background: #fff;
  transition: width 0.3s ease;
}

.main-header.scrolled .nav-link::after {
  background: #5e72e4;
}

.nav-link:hover::after,
.nav-link.router-link-exact-active::after {
  width: 100%;
}

.nav-user {
  margin-left: auto;
  display: flex;
  align-items: center;
}

.nav-button {
  padding: 8px 18px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 20px;
  text-decoration: none;
  margin-left: 12px;
  transition: all 0.3s;
  font-weight: 500;
  background: transparent;
  color: #fff;
}

.main-header.scrolled .nav-button {
  border-color: #dee2e6;
  color: #495057;
}

.nav-button.login:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.main-header.scrolled .nav-button.login:hover {
  background-color: #5e72e4;
  color: #fff;
  border-color: #5e72e4;
}

.nav-button.register {
  background-color: #fff;
  color: #667eea;
  border-color: #fff;
}

.main-header.scrolled .nav-button.register {
  background-color: #5e72e4;
  color: #fff;
  border-color: #5e72e4;
}

.nav-button.register:hover {
  background-color: rgba(255, 255, 255, 0.9);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.main-header.scrolled .nav-button.register:hover {
  background-color: #485ac1;
  border-color: #485ac1;
}

.user-menu {
  position: relative;
  cursor: pointer;
  padding: 10px;
  display: flex;
  align-items: center;
  border-radius: 20px;
  transition: background-color 0.3s;
}

.user-menu:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.main-header.scrolled .user-menu:hover {
  background-color: #f8f9fa;
}

.welcome-text {
  font-weight: 500;
  color: #fff;
  margin-right: 10px;
  margin-left: 12px; /* 添加这一行，增加头像和用户名之间的间距 */
}

.main-header.scrolled .welcome-text {
  color: #495057;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s;
}

.main-header.scrolled .user-avatar {
  border-color: #dee2e6;
}

.user-menu:hover .user-avatar {
  border-color: #fff;
  transform: scale(1.05);
}

.main-header.scrolled .user-menu:hover .user-avatar {
  border-color: #5e72e4;
}

.dropdown {
  opacity: 0;
  visibility: hidden;
  position: absolute;
  right: 0;
  top: 100%;
  background-color: #fff;
  border: none;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  padding: 10px 0;
  min-width: 180px;
  transition: all 0.25s cubic-bezier(0.25, 0.8, 0.25, 1);
  transform: translateY(10px);
  z-index: 1000;
}

.user-menu:hover .dropdown {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  color: #495057;
  text-decoration: none;
  font-size: 14px;
  transition: all 0.2s;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
  color: #5e72e4;
  padding-left: 25px;
}

.dropdown .divider {
  height: 1px;
  background-color: #e9ecef;
  margin: 8px 0;
}

.main-content {
  padding-top: 80px;
}

.main-content.no-padding-top {
  padding-top: 0;
}
</style>